/**
 * IMPORTS
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handleCriarNovoModelo } from "@/domain/use-cases/modelos";

const notifySuccess = (message: string) =>
  toast(message, {
    type: "success",
    position: "top-center",
  });

const notifyError = (message: string) =>
  toast(message, {
    type: "error",
    position: "top-center",
  });

type ICreateModeloResponse = {
  code: number;
  status: string;
  message: string;
  data: {
    user_id: string;
    produto_id: string;
    tecnologia_id: string;
    nome_modelo: string;
    campo_chave: string;
    tipo_entrega: string;
    posicionamento: string;
    tem_furo: boolean;
    tem_dados_variaveis: boolean;
    foto_frente_path: string;
    foto_verso_path: string;
    updated_at: string;
    created_at: string;
    id: number;
  };
  pagination: [];
};

type ICreateModeloPayload = {
  user_id: number;
  produto_id: number;
  nome_modelo: string;
  campo_chave: string;
  tipo_entrega_id: number;
  posicionamento: string;
  tem_furo: boolean;
  tem_carga_foto: boolean;
  tem_dados_variaveis: boolean;
  is_provisorio?: boolean;
  tipo_furo?: string;
  observacoes?: string;
  tecnologia_id: number;
  foto_frente?: File;
  foto_verso?: File;
};

export function useCriarModelo() {
  const queryClient = useQueryClient(); // ← necessário para invalidar a query
  const { token: accessToken } = useStoreZustandUserAuth();

  const mutation = useMutation<ICreateModeloResponse, Error, ICreateModeloPayload>({
    mutationFn: async (payload) => {
      const formData = new FormData();

      formData.append("cliente_id", String(payload.user_id));
      formData.append("produto_id", String(payload.produto_id));
      formData.append("nome_modelo", payload.nome_modelo);
      formData.append("campo_chave", payload.campo_chave);
      formData.append("posicionamento", payload.posicionamento.toLocaleLowerCase());
      formData.append("tem_furo", payload.tem_furo ? "1" : "0");
      formData.append("tem_carga_foto", payload.tem_carga_foto ? "1" : "0");
      formData.append("tem_dados_variaveis", payload.tem_dados_variaveis ? "1" : "0");
      if (payload.is_provisorio !== undefined) {
        formData.append("is_provisorio", payload.is_provisorio ? "1" : "0");
      }
      formData.append("tecnologia_id", String(payload.tecnologia_id)); // ou enviar múltiplos com JSON.stringify se backend aceitar

      // if (payload.tipo_furo) formData.append("tipo_furo", payload.tipo_furo);
      if (payload.observacoes) {
        formData.append("observacoes", payload.observacoes);
      }

      // Se o tipo_furo for "1", adiciona ao FormData
      if (payload.tipo_furo) {
        formData.append("tipo_furo", payload.tipo_furo ?? null);
      }

      if (payload.foto_frente) {
        formData.append("foto_frente", payload.foto_frente);
      }

      if (payload.foto_verso) {
        formData.append("foto_verso", payload.foto_verso);
      }
      return await handleCriarNovoModelo(accessToken!, formData);
    },
    onSuccess: async () => {
      notifySuccess("Modelo registrado com sucesso!");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["modelos-por-cliente"] }),
        queryClient.invalidateQueries({ queryKey: ["modelos"] }),
        queryClient.invalidateQueries({ queryKey: ["modelos-unico"] }),
      ]);
    },
    onError: async () => {
      notifyError("Houve um problema em registrar o modelo!");
    },
  });

  return {
    criarModelo: mutation.mutate, // chamada direta
    criarModeloAsync: mutation.mutateAsync, // se quiser await
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
  };
}
